import React, { useRef, useState } from "react";
import { Logo } from "../components/landing/Logo";
import { MedicallyCuratedCrest } from "../components/landing/MedicallyCuratedCrest";
import { MolecularBackground } from "../components/landing/MolecularBackground";
import { MediaAccessGate } from "../components/landing/MediaAccessGate";
import { Play, Pause, Maximize2, Sparkles, ArrowLeft } from "lucide-react";

const videos = [
  {
    id: "master-promo",
    title: "Master App Promo",
    eyebrow: "Flagship · Intelligent Guidance",
    description:
      "The flagship film — an end-to-end look at how SmartPeptides.ai delivers intelligent research guidance, biomarker analysis, and concierge-level support to every researcher.",
    src: "https://customer-assets.emergentagent.com/jobs/c1c7d5d3-a74f-4f1e-ac43-3ebe4cbaed2c/videos/fcfe9a5b8c9ffbe8.mp4",
    featured: true,
  },
  {
    id: "product-teaser",
    title: "Product Teaser",
    eyebrow: "Brand · Rotating Helix",
    description:
      "A short brand teaser featuring the SmartPeptides peptide-helix motif — for top-of-funnel awareness and partner reels.",
    src: "https://customer-assets.emergentagent.com/jobs/c1c7d5d3-a74f-4f1e-ac43-3ebe4cbaed2c/videos/e5413ed48975700e.mp4",
    featured: false,
  },
  {
    id: "storytelling",
    title: "Storytelling Ad",
    eyebrow: "Narrative · Problem & Solution",
    description:
      "A narrative-driven ad walking through the fragmentation of today's peptide-research landscape — and the clarity SmartPeptides.ai brings.",
    src: "https://customer-assets.emergentagent.com/jobs/c1c7d5d3-a74f-4f1e-ac43-3ebe4cbaed2c/videos/c1288376e8ee946a.mp4",
    featured: false,
  },
  {
    id: "app-teaser",
    title: "App-Only Teaser",
    eyebrow: "Product · Digital UI",
    description:
      "A pure UI showcase — fly-through of the dashboard, protocol planner, and biomarker views. Built for product-led marketing surfaces.",
    src: "https://customer-assets.emergentagent.com/jobs/c1c7d5d3-a74f-4f1e-ac43-3ebe4cbaed2c/videos/38faaf0f8f9b4dd9.mp4",
    featured: false,
  },
  {
    id: "lia-explainer",
    title: "LIA UI Explainer",
    eyebrow: "Feature · Longevity Intelligence Agent",
    description:
      "A deep-dive on LIA — how the Longevity Intelligence Agent surfaces insights, cites sources, and personalizes guidance to every researcher's profile.",
    src: "https://customer-assets.emergentagent.com/jobs/c1c7d5d3-a74f-4f1e-ac43-3ebe4cbaed2c/videos/d653073b2830b8be.mp4",
    featured: false,
  },
];

const VideoCard = ({ video, featured = false }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);

  const togglePlay = () => {
    if (!ref.current) return;
    if (ref.current.paused) {
      ref.current.play();
      setPlaying(true);
    } else {
      ref.current.pause();
      setPlaying(false);
    }
  };

  const fullscreen = () => {
    if (!ref.current) return;
    if (ref.current.requestFullscreen) ref.current.requestFullscreen();
    else if (ref.current.webkitEnterFullscreen) ref.current.webkitEnterFullscreen();
  };

  return (
    <article
      className={[
        "group relative rounded-2xl overflow-hidden navy-card-bg border border-white/12 hover:border-[hsl(var(--gold))]/45 transition-all flex flex-col",
        featured ? "lg:col-span-2" : "",
      ].join(" ")}
      data-testid={`video-card-${video.id}`}
    >
      <div className="relative aspect-video bg-black">
        <video
          ref={ref}
          src={video.src}
          preload="metadata"
          playsInline
          onEnded={() => setPlaying(false)}
          onPause={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid={`video-player-${video.id}`}
        />

        {/* gradient overlay only when not playing */}
        {!playing && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent pointer-events-none" />
        )}

        {/* Play / pause big button */}
        <button
          type="button"
          onClick={togglePlay}
          aria-label={playing ? "Pause video" : "Play video"}
          data-testid={`video-toggle-${video.id}`}
          className="absolute inset-0 flex items-center justify-center transition-opacity opacity-100 group-hover:opacity-100"
        >
          <span
            className={[
              "inline-flex items-center justify-center rounded-full gold-bg text-[hsl(var(--navy-deep))] shadow-2xl transition-all",
              featured ? "h-20 w-20" : "h-16 w-16",
              playing ? "opacity-0 group-hover:opacity-100 scale-90" : "opacity-100 scale-100",
            ].join(" ")}
          >
            {playing ? <Pause className="h-7 w-7" /> : <Play className="h-7 w-7 ml-1" />}
          </span>
        </button>

        {/* Fullscreen button */}
        <button
          type="button"
          onClick={fullscreen}
          aria-label="Fullscreen"
          data-testid={`video-fullscreen-${video.id}`}
          className="absolute top-4 right-4 inline-flex items-center justify-center h-9 w-9 rounded-md bg-black/60 backdrop-blur text-white/85 hover:bg-black/80 hover:text-white transition-colors"
        >
          <Maximize2 className="h-4 w-4" />
        </button>

        {video.featured && (
          <span className="absolute top-4 left-4 inline-flex items-center gap-1 rounded-full gold-bg px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-bold text-[hsl(var(--navy-deep))]">
            <Sparkles className="h-3 w-3" />
            Featured
          </span>
        )}
      </div>

      <div className="p-7">
        <p className="text-xs uppercase tracking-[0.18em] gold-text font-medium">
          {video.eyebrow}
        </p>
        <h3 className={`mt-2 font-serif text-white leading-tight ${featured ? "text-3xl lg:text-4xl" : "text-2xl"}`}>
          {video.title}
        </h3>
        <p className="mt-3 text-sm text-white/65 leading-relaxed">
          {video.description}
        </p>
      </div>
    </article>
  );
};

export default function MediaHub() {
  const featured = videos.find((v) => v.featured);
  const rest = videos.filter((v) => !v.featured);

  return (
    <div className="min-h-screen bg-background text-white" data-testid="media-hub-page">
      <MediaAccessGate />
      {/* Top bar */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[hsl(var(--navy-deep))]/85 border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
          <Logo dark />
          <a
            href="/"
            data-testid="media-back-home"
            className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-20 pb-16 lg:pt-28 lg:pb-20 overflow-hidden" data-testid="media-hero">
        <MolecularBackground />
        <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
          <div className="flex flex-wrap items-center justify-center gap-3 mb-7">
            <div className="inline-flex items-center gap-2 rounded-full border gold-border bg-[hsl(var(--gold))]/10 px-3.5 py-1.5 text-xs font-medium gold-text">
              <Sparkles className="h-3.5 w-3.5" />
              Partner Press Kit · Confidential
            </div>
            <MedicallyCuratedCrest size={56} />
          </div>

          <h1 className="text-center font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-white">
            SmartPeptides.ai <br />
            <span className="gold-text italic">Media Hub.</span>
          </h1>
          <p className="mt-7 text-center text-lg sm:text-xl text-white/75 leading-relaxed max-w-2xl mx-auto">
            A curated collection of clinical and product films for partners,
            physicians, and collaborators. Watch, download, or share — all assets
            available in HD.
          </p>
        </div>
      </section>

      {/* Featured video */}
      {featured && (
        <section className="relative pb-10" data-testid="media-featured-section">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="grid lg:grid-cols-2 gap-6">
              <VideoCard video={featured} featured />
            </div>
          </div>
        </section>
      )}

      {/* Grid of rest */}
      <section className="relative pb-24 lg:pb-32" data-testid="media-grid-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
            {rest.map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>

          <div className="mt-20 rounded-2xl navy-card-bg border border-white/12 p-10 text-center">
            <p className="text-xs uppercase tracking-[0.18em] gold-text font-semibold">
              Partnership inquiries
            </p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-white">
              Want to feature these in your <span className="gold-text italic">channel or clinic?</span>
            </h2>
            <p className="mt-4 text-base text-white/65 max-w-xl mx-auto">
              These films are available for partners, physicians, and approved
              collaborators. Reach out and we'll send you the original master files
              with brand-safe usage guidelines.
            </p>
            <a
              href="mailto:hello@smartpeptides.ai?subject=Media%20Hub%20—%20Partner%20Inquiry"
              data-testid="media-contact-cta"
              className="mt-7 inline-flex items-center gap-2 rounded-md gold-bg h-12 px-6 text-sm font-semibold text-[hsl(var(--navy-deep))] hover:opacity-90 transition-opacity"
            >
              Request partnership access
            </a>
          </div>
        </div>
      </section>

      {/* Footer (minimal, branded) */}
      <footer className="border-t border-white/10 py-10">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <MedicallyCuratedCrest size={48} />
            <div>
              <span className="text-xs uppercase tracking-[0.18em] gold-text font-medium">
                Curated by medical professionals
              </span>
              <p className="text-xs text-white/50 mt-0.5">
                For research and education purposes only.
              </p>
            </div>
          </div>
          <p className="text-xs text-white/50">
            © {new Date().getFullYear()} SmartPeptides.ai — All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
